import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

export type ScopeItem = { trade: string; description: string; confidence: "low" | "medium" | "high" };
export type LineItem = {
  name: string;
  unit: string;
  qty: number;
  unit_cost: number;
  margin_percent: number;
  customer_price: number;
  notes?: string;
};
export type EstimateTrade = {
  trade_code: string;
  line_items: LineItem[];
  subtotal: number;
  cost: number;
  profit: number;
};
export type Estimate = {
  trades: EstimateTrade[];
  grand_total: number;
  total_cost: number;
  total_profit: number;
};

export type UploadedFile = {
  id: string;
  name: string;
  url: string;
  storagePath: string;
  kind: "image" | "video" | "blueprint";
  mimeType: string;
  analysisFrames?: string[];
  blueprintExtracted?: boolean;
  blueprintSheetCount?: number | null;
  blueprintScopeCount?: number | null;
  blueprintError?: string | null;
};

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  pending?: boolean;
};

export type MeasurementRequest = {
  trade: string;
  fields: { key: string; label: string; type: "number" | "text" }[];
};

const dedupeScopeItems = (items: ScopeItem[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = `${item.trade}::${item.description.trim().toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export function useAiJobBuilder() {
  const { user, orgId } = useAuth();
  const navigate = useNavigate();
  const scopeRequestRef = useRef<Promise<any> | null>(null);
  const lastScopeSignatureRef = useRef("");
  const lastChatFingerprintRef = useRef("");
  const chatRef = useRef<ChatMessage[]>([
    {
      id: crypto.randomUUID(),
      role: "assistant",
      content:
        "Hey — I'm Bridgette. Tell me about this job (storm damage, blueprint, walkthrough video, anything) and drop in photos or video. I'll detect the trades, draft scope, build pricing, and we'll spin the project up together. What do you have?",
    },
  ]);

  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [notes, setNotes] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [ownerName, setOwnerName] = useState("");

  const [detectedTrades, setDetectedTrades] = useState<string[]>([]);
  const [scopeItems, setScopeItems] = useState<ScopeItem[]>([]);
  const [aiSummary, setAiSummary] = useState("");
  const [scopeLoading, setScopeLoading] = useState(false);

  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [proposal, setProposal] = useState<{ id: string; share_id: string; title: string } | null>(null);
  const [proposalLoading, setProposalLoading] = useState(false);

  const [chat, setChat] = useState<ChatMessage[]>(chatRef.current);
  const [chatLoading, setChatLoading] = useState(false);
  const [measurementRequest, setMeasurementRequest] = useState<MeasurementRequest | null>(null);
  const [measurements, setMeasurements] = useState<Record<string, Record<string, any>>>({});

  const extractPdfText = useCallback(async (file: File) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = "";

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => ("str" in item ? item.str : ""))
        .filter(Boolean)
        .join(" ");
      fullText += `\n[Sheet/Page ${i}]\n${pageText}\n`;
    }

    return {
      text: fullText.trim(),
      pageCount: pdf.numPages,
    };
  }, []);

  const extractVideoFrames = useCallback((file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const objectUrl = URL.createObjectURL(file);

      video.src = objectUrl;
      video.crossOrigin = "anonymous";
      video.muted = true;
      video.playsInline = true;
      video.preload = "metadata";

      video.onloadedmetadata = async () => {
        try {
          if (!isFinite(video.duration) || video.duration <= 0) {
            throw new Error("Could not read video duration");
          }

          const frameCount = Math.max(4, Math.min(8, Math.floor(video.duration / 15) || 4));
          const step = video.duration / (frameCount + 1);
          const timestamps = Array.from({ length: frameCount }, (_, index) => step * (index + 1));

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Could not prepare video frame extraction");

          const captures: string[] = [];

          const captureAt = (time: number) =>
            new Promise<void>((resolveCapture, rejectCapture) => {
              const handleSeeked = () => {
                try {
                  const maxWidth = 1280;
                  const scale = Math.min(1, maxWidth / (video.videoWidth || maxWidth));
                  canvas.width = Math.round((video.videoWidth || maxWidth) * scale);
                  canvas.height = Math.round((video.videoHeight || 720) * scale);
                  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                  captures.push(canvas.toDataURL("image/jpeg", 0.78));
                  video.removeEventListener("seeked", handleSeeked);
                  resolveCapture();
                } catch (error) {
                  video.removeEventListener("seeked", handleSeeked);
                  rejectCapture(error);
                }
              };

              video.addEventListener("seeked", handleSeeked, { once: true });
              video.currentTime = time;
            });

          for (const timestamp of timestamps) {
            await captureAt(timestamp);
          }

          URL.revokeObjectURL(objectUrl);
          resolve(captures);
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          reject(error);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load video for AI analysis"));
      };
    });
  }, []);

  const extractBlueprint = useCallback(
    async (storagePath: string, fileName: string, extractedText?: string, pageCount?: number) => {
      try {
        const { data, error } = await supabase.functions.invoke("jobs-ai-extract-blueprint", {
          body: {
            storage_path: storagePath,
            bucket: "job-photos",
            file_name: fileName,
            extracted_text: extractedText,
            page_count: pageCount,
          },
        });
        if (error) throw error;
        if (!data?.ok) throw new Error(data?.error || "Blueprint extraction failed");

        const newTrades: string[] = Array.isArray(data.trades) ? data.trades : [];
        const newScope: ScopeItem[] = Array.isArray(data.scope_items) ? data.scope_items : [];
        if (!newScope.length) {
          throw new Error(data?.detail || "Blueprint read finished but returned 0 scope items");
        }

        if (data.summary) {
          setAiSummary((prev) => (prev ? `${prev}\n\n${data.summary}` : data.summary));
        }
        setDetectedTrades((prev) => Array.from(new Set([...prev, ...newTrades])));
        setSelectedTrades((prev) => Array.from(new Set([...prev, ...newTrades])));
        setScopeItems((prev) => dedupeScopeItems([...prev, ...newScope]));

        toast.success(
          `Blueprint read · ${newTrades.length} trade${newTrades.length === 1 ? "" : "s"} · ${newScope.length} scope items`,
          { duration: 1500 },
        );
        return { trades: newTrades, scope_items: newScope, sheet_count: data.sheet_count ?? null };
      } catch (e: any) {
        console.error("Blueprint extraction failed", e);
        toast.error(e.message || "Could not read blueprint", { duration: 1500 });
        return null;
      }
    },
    [],
  );

  const uploadFiles = useCallback(
    async (selected: File[]) => {
      if (!orgId || !user) return;
      const next: UploadedFile[] = [];
      for (const f of selected) {
        const ext = f.name.split(".").pop() || "bin";
        const path = `${orgId}/ai-builder/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage.from("job-photos").upload(path, f, {
          cacheControl: "3600",
          upsert: false,
        });
        if (error) {
          toast.error(`Upload failed: ${f.name}`);
          continue;
        }
        const { data } = supabase.storage.from("job-photos").getPublicUrl(path);

        const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
        const kind: UploadedFile["kind"] = isPdf
          ? "blueprint"
          : f.type.startsWith("video/")
            ? "video"
            : "image";

        let analysisFrames: string[] | undefined;
        let extractedBlueprintText: string | undefined;
        let blueprintPageCount: number | undefined;
        if (kind === "video") {
          try {
            analysisFrames = await extractVideoFrames(f);
          } catch (error) {
            console.error("Video frame extraction failed:", error);
            toast.error(`Uploaded ${f.name}, but AI could not read video keyframes`);
          }
        } else if (kind === "blueprint") {
          try {
            const extracted = await extractPdfText(f);
            extractedBlueprintText = extracted.text;
            blueprintPageCount = extracted.pageCount;
          } catch (error) {
            console.error("Blueprint text extraction failed:", error);
          }
        }

        const entry: UploadedFile = {
          id: crypto.randomUUID(),
          name: f.name,
          url: data.publicUrl,
          storagePath: path,
          kind,
          mimeType:
            f.type ||
            (kind === "video"
              ? "video/mp4"
              : kind === "blueprint"
                ? "application/pdf"
                : "image/jpeg"),
          analysisFrames,
          blueprintExtracted: kind === "blueprint" ? false : undefined,
          blueprintError: null,
        };
        next.push(entry);

        if (kind === "blueprint") {
          // Fire & forget — UI shows the file pill immediately, extraction
          // populates scope/trades when it finishes.
          toast.message(`Reading blueprint: ${f.name}…`, { duration: 1500 });
          extractBlueprint(path, f.name, extractedBlueprintText, blueprintPageCount).then((res) => {
            setFiles((prev) =>
              prev.map((row) => {
                if (row.id !== entry.id) return row;
                return res
                  ? {
                      ...row,
                      blueprintExtracted: true,
                      blueprintSheetCount: res.sheet_count,
                      blueprintScopeCount: res.scope_items.length,
                      blueprintError: null,
                    }
                  : {
                      ...row,
                      blueprintExtracted: false,
                      blueprintSheetCount: null,
                      blueprintScopeCount: null,
                      blueprintError: "Read failed",
                    };
              }),
            );
          });
        }
      }
      setFiles((prev) => [...prev, ...next]);
      return next;
    },
    [extractBlueprint, extractPdfText, extractVideoFrames, orgId, user],
  );

  const detectScope = useCallback(async () => {
    const image_urls = [
      ...files.filter((f) => f.kind === "image").map((f) => f.url),
      ...files
        .filter((f) => f.kind === "video")
        .flatMap((f) => f.analysisFrames || []),
    ].slice(0, 12);
    const scopeSignature = JSON.stringify({ image_urls, notes: notes.trim() });
    const hasDetectedScope = Boolean(scopeItems.length || aiSummary || detectedTrades.length);

    if (scopeRequestRef.current) return scopeRequestRef.current;
    if (!image_urls.length) return null;
    if (hasDetectedScope && lastScopeSignatureRef.current === scopeSignature) {
      return {
        summary: aiSummary,
        trades: detectedTrades,
        scope_items: scopeItems,
      };
    }

      const request = (async () => {
      setScopeLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("jobs-ai-scope-detect", {
          body: { image_urls, notes },
        });
        if (error) throw error;
        lastScopeSignatureRef.current = scopeSignature;

        const nextTrades = Array.isArray(data?.trades) ? data.trades : [];
        const nextScope = Array.isArray(data?.scope_items) ? data.scope_items : [];

        setAiSummary((prev) => {
          const incoming = data?.summary?.trim?.() || "";
          if (!incoming) return prev;
          return prev ? `${prev}\n\n${incoming}` : incoming;
        });
        setDetectedTrades((prev) => Array.from(new Set([...prev, ...nextTrades])));
        setScopeItems((prev) => dedupeScopeItems([...prev, ...nextScope]));
        setSelectedTrades((prev) => Array.from(new Set([...prev, ...nextTrades])));

        return {
          ...data,
          trades: Array.from(new Set([...(detectedTrades || []), ...nextTrades])),
          scope_items: dedupeScopeItems([...(scopeItems || []), ...nextScope]),
        };
      } catch (e: any) {
        toast.error(e.message || "Scope detection failed");
        return null;
      } finally {
        setScopeLoading(false);
        scopeRequestRef.current = null;
      }
    })();

    scopeRequestRef.current = request;
    return request;
  }, [files, notes, scopeItems, aiSummary, detectedTrades]);

  const generateEstimate = useCallback(async () => {
    if (!selectedTrades.length) {
      toast.error("Pick at least one trade first");
      return;
    }
    setEstimateLoading(true);
    try {
      const scopeText = [
        notes,
        aiSummary,
        ...scopeItems
          .filter((s) => selectedTrades.includes(s.trade))
          .map((s) => `[${s.trade}] ${s.description}`),
      ]
        .filter(Boolean)
        .join("\n");
      const { data, error } = await supabase.functions.invoke("jobs-ai-generate-estimate", {
        body: {
          trades: selectedTrades,
          scope_notes: scopeText,
          property_summary: propertyAddress,
        },
      });
      if (error) throw error;
      setEstimate(data);
      toast.success("Estimate ready");
      return data as Estimate;
    } catch (e: any) {
      toast.error(e.message || "Estimate generation failed");
      return null;
    } finally {
      setEstimateLoading(false);
    }
  }, [selectedTrades, notes, aiSummary, scopeItems, propertyAddress]);

  const createJob = useCallback(async (options?: { openEstimate?: boolean }) => {
    if (!user || !orgId) return;
    if (!ownerName || !propertyAddress) {
      toast.error("Owner name and property address required");
      return;
    }
    setCreatingJob(true);
    try {
      const jobNumber = `AI-${Date.now().toString().slice(-6)}`;
      const { data: job, error } = await supabase
        .from("jobs")
        .insert([
          {
            org_id: orgId,
            job_number: jobNumber,
            owner_name: ownerName,
            property_address: propertyAddress,
            property_type: "Residential",
            status: "new" as const,
            created_by: user.id,
            estimated_value: estimate?.grand_total ?? 0,
            notes: aiSummary
              ? `[AI Builder]\n${aiSummary}\n\n${notes || ""}`.trim()
              : notes || null,
          },
        ])
        .select()
        .single();
      if (error) throw error;

      // Attach trades
      const tradesPayload = (selectedTrades.length ? selectedTrades : ["general_construction"]).map(
        (trade_code) => ({
          job_id: job.id,
          org_id: orgId,
          trade_code,
          status: "new",
          subtotal:
            estimate?.trades.find((t) => t.trade_code === trade_code)?.subtotal ?? 0,
        }),
      );
      await supabase.from("job_trades").insert(tradesPayload);

      // Attach uploaded photos
      if (files.length) {
        await supabase.from("photos").insert(
          files.map((f) => ({
            job_id: job.id,
            org_id: orgId,
            url: f.url,
            storage_path: f.storagePath,
            filename: f.name,
            mime_type: f.mimeType,
            uploaded_by: user.id,
          })),
        );
      }

      // If estimate exists, persist a draft job_estimate per trade with line items
      if (estimate) {
        for (const t of estimate.trades) {
          const overheadAmount = +(t.cost * 0.10).toFixed(2);
          const { data: est, error: estErr } = await supabase
            .from("job_estimates")
            .insert({
              job_id: job.id,
              org_id: orgId,
              label: `AI draft – ${t.trade_code}`,
              status: "draft",
              total_squares: 0,
              waste_percent: 10,
              material_rate: 0,
              labor_rate: 0,
              tear_off_cost: 0,
              dump_fee: 0,
              overhead_percent: 10,
              profit_percent: 15,
              cost_subtotal: t.cost,
              overhead_amount: overheadAmount,
              profit_amount: t.profit,
              customer_price: t.subtotal,
            })
            .select()
            .single();
          if (estErr || !est) continue;
          if (t.line_items.length) {
            await supabase.from("estimate_line_items").insert(
              t.line_items.map((li, idx) => ({
                estimate_id: est.id,
                item_name: li.name,
                description: li.notes ?? null,
                quantity: li.qty,
                unit_type: li.unit,
                unit_price: li.unit_cost,
                total_cost: li.customer_price,
                is_auto_generated: true,
                line_order: idx,
              })),
            );
          }
        }
      }

      await supabase.from("job_timeline").insert({
        job_id: job.id,
        org_id: orgId,
        event_type: "job_created",
        description: `Job created via AI Builder: ${ownerName}`,
        created_by: user.id,
      });

      toast.success("Job created!");
      navigate(options?.openEstimate ? `/jobs/${job.id}?tab=Estimates` : `/jobs/${job.id}`);
      return job.id as string;
    } catch (e: any) {
      toast.error(e.message || "Failed to create job");
      return null;
    } finally {
      setCreatingJob(false);
    }
  }, [
    user,
    orgId,
    ownerName,
    propertyAddress,
    notes,
    aiSummary,
    selectedTrades,
    estimate,
    files,
    navigate,
  ]);

  const toggleTrade = useCallback((code: string) => {
    setSelectedTrades((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  // ----- Scope editing -----
  const updateScopeItem = useCallback((index: number, patch: Partial<ScopeItem>) => {
    setScopeItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }, []);
  const removeScopeItem = useCallback((index: number) => {
    setScopeItems((prev) => prev.filter((_, i) => i !== index));
  }, []);
  const addScopeItem = useCallback((item: ScopeItem) => {
    setScopeItems((prev) => [...prev, item]);
    setSelectedTrades((prev) => (prev.includes(item.trade) ? prev : [...prev, item.trade]));
  }, []);

  const saveMeasurement = useCallback((trade: string, values: Record<string, any>) => {
    setMeasurements((prev) => ({ ...prev, [trade]: { ...(prev[trade] || {}), ...values } }));
    setMeasurementRequest(null);
  }, []);

  // ----- Conversational AI driver -----
  const appendAssistant = useCallback((content: string) => {
    setChat((prev) => {
      const nextMessage: ChatMessage = { id: crypto.randomUUID(), role: "assistant", content };
      const next = [...prev, nextMessage];
      chatRef.current = next;
      return next;
    });
  }, []);

  const buildProjectSnapshot = useCallback(() => ({
    ownerName,
    propertyAddress,
    notes,
    aiSummary,
    fileSummary: {
      images: files.filter((f) => f.kind === "image").length,
      videos: files.filter((f) => f.kind === "video").length,
      blueprints: files.filter((f) => f.kind === "blueprint").length,
    },
    detectedTrades,
    selectedTrades,
    scopeItems,
    measurements,
    estimate: estimate
      ? {
          grand_total: estimate.grand_total,
          total_cost: estimate.total_cost,
          total_profit: estimate.total_profit,
          trades: estimate.trades.map((t) => ({ trade_code: t.trade_code, subtotal: t.subtotal })),
        }
      : null,
    proposal: proposal ? { id: proposal.id, share_id: proposal.share_id, title: proposal.title } : null,
  }), [ownerName, propertyAddress, notes, aiSummary, files, detectedTrades, selectedTrades, scopeItems, measurements, estimate, proposal]);

  const buildProposal = useCallback(async (tier_mode: 'single' | 'tiered' = 'tiered') => {
    if (!estimate) {
      toast.error("Build the estimate first");
      return null;
    }
    if (!orgId) return null;
    if (!ownerName || !propertyAddress) {
      toast.error("I need owner name and property address before I can write the proposal");
      return null;
    }
    setProposalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("jobs-ai-build-proposal", {
        body: {
          org_id: orgId,
          owner_name: ownerName,
          property_address: propertyAddress,
          notes,
          ai_summary: aiSummary,
          scope_items: scopeItems,
          selected_trades: selectedTrades.length ? selectedTrades : ["general_construction"],
          measurements,
          estimate,
          cover_photo_url: files.find((f) => f.kind === "image")?.url,
          tier_mode,
        },
      });
      if (error) throw error;
      const built = { id: data.id, share_id: data.share_id, title: data.title };
      setProposal(built);
      toast.success("Proposal built — share link ready");
      return built;
    } catch (e: any) {
      toast.error(e.message || "Proposal build failed");
      return null;
    } finally {
      setProposalLoading(false);
    }
  }, [orgId, ownerName, propertyAddress, notes, aiSummary, scopeItems, selectedTrades, measurements, estimate, files]);

  const buildActionReply = useCallback((action: any, result: any) => {
    switch (action?.type) {
      case "detect_scope": {
        const tradeList = Array.isArray(result?.trades) && result.trades.length
          ? result.trades.map((t: string) => t.replace(/_/g, " ")).join(", ")
          : "no trades yet";
        const scopeCount = Array.isArray(result?.scope_items) ? result.scope_items.length : 0;
        return `I pulled ${scopeCount} scope items and mapped the trades: ${tradeList}. Review the scope on the right and tell me what to add, remove, or price next.`;
      }
      case "generate_estimate":
        return result
          ? `I built the estimate. Total sell is ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(result.grand_total || 0)} and each line item price is in the estimate panel on the right. Want me to revise any item or turn it into a proposal?`
          : "I couldn't build the estimate yet. Give me the missing details and I'll price it out.";
      case "build_proposal":
        return result
          ? `The proposal is ready with a share link. Want to open it, send it, or create the project now?`
          : "I couldn't build the proposal yet. Let me know whether you want single price or 3 tiers.";
      case "request_measurements":
        return `I need the requested measurements before I can price this correctly.`;
      default:
        return null;
    }
  }, []);

  const runAction = useCallback(async (action: any) => {
    if (!action?.type) return;
    switch (action.type) {
      case "set_field": {
        const { field, value } = action.payload || {};
        if (field === "ownerName") setOwnerName(String(value || ""));
        else if (field === "propertyAddress") setPropertyAddress(String(value || ""));
        else if (field === "notes") setNotes(String(value || ""));
        return { field, value };
      }
      case "set_trades": {
        const trades = Array.isArray(action.payload?.trades) ? action.payload.trades : [];
        setSelectedTrades(trades);
        return { trades };
      }
      case "update_scope": {
        const { operation, index, item } = action.payload || {};
        if (operation === "add" && item) addScopeItem(item);
        else if (operation === "edit" && typeof index === "number" && item) updateScopeItem(index, item);
        else if (operation === "remove" && typeof index === "number") removeScopeItem(index);
        return { operation, index, item };
      }
      case "request_measurements": {
        setMeasurementRequest({
          trade: action.payload?.trade || selectedTrades[0] || "general_construction",
          fields: action.payload?.fields || [],
        });
        return action.payload || null;
      }
      case "detect_scope": return await detectScope();
      case "generate_estimate": return await generateEstimate();
      case "build_proposal": {
        const mode = action.payload?.tier_mode === "single" ? "single" : "tiered";
        return await buildProposal(mode);
      }
      case "ready_to_create": return action.payload || null;
    }
  }, [addScopeItem, detectScope, generateEstimate, buildProposal, removeScopeItem, selectedTrades, updateScopeItem]);

  const callChat = useCallback(async (history: { role: "user" | "assistant"; content: string }[]) => {
    const { data, error } = await supabase.functions.invoke("jobs-ai-chat", {
      body: { messages: history, project: buildProjectSnapshot() },
    });
    if (error) throw error;
    const deferInitialReply = ["detect_scope", "generate_estimate", "build_proposal"].includes(data?.action?.type);
    if (data?.reply && !deferInitialReply) appendAssistant(data.reply);
    if (data?.action) {
      const actionResult = await runAction(data.action);
      const actionReply = buildActionReply(data.action, actionResult);

      if (actionReply) {
        appendAssistant(actionReply);
      } else if (data?.reply && deferInitialReply) {
        appendAssistant(data.reply);
      }
    }
  }, [appendAssistant, buildActionReply, buildProjectSnapshot, runAction]);

  const sendChat = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: "user", content: trimmed };
    const nextChat = [...chatRef.current, userMsg];
    chatRef.current = nextChat;
    setChat(nextChat);
    setChatLoading(true);
    try {
      const history = nextChat.map((m) => ({ role: m.role, content: m.content }));
      await callChat(history);
    } catch (e: any) {
      appendAssistant(`Hmm, I hit a snag: ${e.message || "AI request failed"}. Want to try again?`);
    } finally {
      setChatLoading(false);
    }
  }, [callChat, appendAssistant]);

  const notifyAi = useCallback(async (note: string) => {
    const fingerprint = note.trim();
    if (!fingerprint || lastChatFingerprintRef.current === fingerprint) return;
    lastChatFingerprintRef.current = fingerprint;
    setChatLoading(true);
    try {
      const history = [
        ...chatRef.current.map((m) => ({ role: m.role, content: m.content })),
        { role: "user" as const, content: `(system) ${note}` },
      ];
      await callChat(history);
    } catch {
      // silent
    } finally {
      setChatLoading(false);
    }
  }, [callChat]);

  return {
    // state
    files, notes, setNotes, propertyAddress, setPropertyAddress, ownerName, setOwnerName,
    detectedTrades, scopeItems, aiSummary, selectedTrades, estimate,
    chat, chatLoading, measurementRequest, measurements,
    proposal,
    // status
    scopeLoading, estimateLoading, creatingJob, proposalLoading,
    // actions
    uploadFiles, removeFile, detectScope, toggleTrade, setSelectedTrades, generateEstimate, createJob,
    updateScopeItem, removeScopeItem, addScopeItem, saveMeasurement, setMeasurementRequest,
    sendChat, notifyAi, buildProposal,
  };
}
